import { FC } from 'react';
import { InvestorProfile } from '@/types/index';

interface Props {
  profile: Partial<InvestorProfile>;
  onChange: (data: Partial<InvestorProfile>) => void;
}

const STRATEGIES = [
  { value: 'LOCATIF', label: 'Achat pour louer',             icon: '🏘️' },
  { value: 'FLIP',    label: 'Achat · rénovation · revente', icon: '🔨' },
];

function NumField({ label, unit, value, onChange, min = 0, step = 1, placeholder }: {
  label: string; unit?: string; value: number | null | undefined;
  onChange: (v: number | null) => void; min?: number; step?: number; placeholder?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className="text-sm text-gray-600 dark:text-gray-400 flex-1">{label}</label>
      <div className="flex items-center gap-1">
        <input
          type="number"
          min={min}
          step={step}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="w-28 px-2 py-1.5 text-sm text-right border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:outline-none"
        />
        {unit && <span className="text-xs text-gray-400 w-10">{unit}</span>}
      </div>
    </div>
  );
}

export const Step2Strategie: FC<Props> = ({ profile, onChange }) => {
  const strategies = (profile.strategies as string[]) ?? [];
  const isFlip = strategies.includes('FLIP');

  const toggleStrategy = (val: string) => {
    const updated = strategies.includes(val)
      ? strategies.filter(s => s !== val)
      : [...strategies, val];
    onChange({ strategies: updated });
  };

  return (
    <div className="space-y-8">
      {/* Sélection des stratégies */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Stratégies visées</h3>
        <div className="flex flex-wrap gap-3">
          {STRATEGIES.map(s => (
            <button
              key={s.value}
              onClick={() => toggleStrategy(s.value)}
              className={`px-5 py-3 rounded-xl border-2 text-sm font-medium transition-all flex items-center gap-2
                ${strategies.includes(s.value)
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-blue-300'}`}
            >
              <span className="text-xl">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>
        {strategies.length === 0 && (
          <p className="text-xs text-gray-400 mt-2">Aucune sélection = toutes les stratégies acceptées</p>
        )}
      </div>

      {/* Paramètres LOCATIF — affichés si LOCATIF sélectionné ou aucune stratégie */}
      {(strategies.includes('LOCATIF') || strategies.length === 0) && (
        <div className="p-4 rounded-2xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-2">
              🏘️ Stratégie : Achat pour louer
            </h3>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              Paramètres utilisés pour évaluer la rentabilité locative et scorer automatiquement les opportunités.
            </p>
          </div>

          {/* Formule ROE */}
          <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 text-xs text-blue-700 dark:text-blue-300 space-y-1">
            <p className="font-semibold">Formule ROE</p>
            <p>Cash-flow = Loyers − Charges − Impôts − Intérêts − Amortissement</p>
            <p>ROE (%) = (Cash-flow / Fonds propres) × 100</p>
          </div>

          <div className="space-y-3">
            <NumField
              label="ROE cible (%)"
              unit="%"
              value={profile.roeTarget}
              onChange={v => onChange({ roeTarget: v ?? 0 })}
              min={0}
              step={0.1}
              placeholder="5"
            />
            <NumField
              label="Taux hypothécaire (%)"
              unit="%"
              value={profile.mortgageRate ?? undefined}
              onChange={v => onChange({ mortgageRate: v ?? undefined })}
              min={0}
              step={0.01}
              placeholder="1.8"
            />
            <NumField
              label="Taux stress test (%)"
              unit="%"
              value={profile.mortgageRateStressTest ?? undefined}
              onChange={v => onChange({ mortgageRateStressTest: v ?? undefined })}
              min={0}
              step={0.01}
              placeholder="5"
            />
            <NumField
              label="Taux d'impôt foncier (%)"
              unit="%"
              value={profile.propertyTaxRate ?? undefined}
              onChange={v => onChange({ propertyTaxRate: v ?? undefined })}
              min={0}
              step={0.01}
              placeholder="0.15"
            />
            <NumField
              label="Taux d'amortissement (%)"
              unit="%"
              value={profile.amortizationRate ?? undefined}
              onChange={v => onChange({ amortizationRate: v ?? undefined })}
              min={0}
              step={0.01}
              placeholder="1"
            />
            <NumField
              label="Taux de maintenance (%)"
              unit="%"
              value={profile.maintenanceRate ?? undefined}
              onChange={v => onChange({ maintenanceRate: v ?? undefined })}
              min={0}
              step={0.01}
              placeholder="0.5"
            />
            <NumField
              label="Frais d'acquisition (%)"
              unit="%"
              value={profile.acquisitionFees ?? undefined}
              onChange={v => onChange({ acquisitionFees: v ?? undefined })}
              min={0}
              step={0.1}
              placeholder="5"
            />
            <div className="flex items-center gap-3 pt-1">
              <input
                type="checkbox"
                id="equityIncludesAcquisition"
                checked={profile.equityIncludesAcquisition ?? false}
                onChange={e => onChange({ equityIncludesAcquisition: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="equityIncludesAcquisition" className="text-sm text-gray-600 dark:text-gray-400">
                Frais d'acquisition inclus dans les fonds propres
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Paramètres flip — affichés uniquement si FLIP est sélectionné */}
      {isFlip && (
        <div className="p-4 rounded-2xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/10 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-orange-700 dark:text-orange-400 flex items-center gap-2">
              🔨 Stratégie : Achat · rénovation · revente
            </h3>
            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
              Paramètres utilisés pour estimer la viabilité d'un projet de rénovation-revente.
            </p>
          </div>

          <div className="space-y-3">
            <NumField
              label="Facteur prix de vente potentiel"
              unit="(0–1)"
              value={profile.flipFacteurVente}
              onChange={v => onChange({ flipFacteurVente: v })}
              min={0}
              step={0.05}
              placeholder="0.65"
            />
            <p className="text-xs text-gray-400 -mt-1">
              Ex: 0.65 → prix vente = surface × prix marché local × 65%
            </p>

            <div className="border-t border-orange-200 dark:border-orange-800 pt-3 space-y-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Forfaits fixes</p>
              <NumField
                label="Cuisine"
                unit="CHF"
                value={profile.flipBudgetCuisine}
                onChange={v => onChange({ flipBudgetCuisine: v })}
                placeholder="15000"
              />
              <NumField
                label="Salle de bain"
                unit="CHF"
                value={profile.flipBudgetSalleBain}
                onChange={v => onChange({ flipBudgetSalleBain: v })}
                placeholder="12000"
              />
            </div>

            <div className="border-t border-orange-200 dark:border-orange-800 pt-3 space-y-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Coûts unitaires</p>
              <NumField
                label="Menuiserie"
                unit="CHF/pièce"
                value={profile.flipCoutMenuiserie}
                onChange={v => onChange({ flipCoutMenuiserie: v })}
                placeholder="2000"
              />
              <NumField
                label="Sol"
                unit="CHF/m²"
                value={profile.flipCoutSol}
                onChange={v => onChange({ flipCoutSol: v })}
                placeholder="80"
              />
              <NumField
                label="Électricité"
                unit="CHF/m²"
                value={profile.flipCoutElectricite}
                onChange={v => onChange({ flipCoutElectricite: v })}
                placeholder="50"
              />
              <NumField
                label="Marge souhaitée sur prix de vente"
                unit="%"
                value={profile.flipMargeVoulue ?? 10}
                onChange={v => onChange({ flipMargeVoulue: v })}
                min={0}
                step={1}
                placeholder="10"
              />
            </div>

            <div className="border-t border-amber-200 dark:border-amber-700 pt-3 space-y-3">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Frais</p>
              <NumField
                label="Frais d'achat"
                unit="%"
                value={profile.flipFraisAchat ?? 5}
                onChange={v => onChange({ flipFraisAchat: v })}
                min={0}
                step={0.5}
                placeholder="5"
              />
              <NumField
                label="Frais de revente"
                unit="%"
                value={profile.flipFraisRevente ?? 3}
                onChange={v => onChange({ flipFraisRevente: v })}
                min={0}
                step={0.5}
                placeholder="3"
              />
              <NumField
                label="Intérêts emprunt rénovation (annuel)"
                unit="%"
                value={profile.flipFraisEmprunt ?? 2}
                onChange={v => onChange({ flipFraisEmprunt: v })}
                min={0}
                step={0.5}
                placeholder="2"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
